<template>
  <BContainer
    v-if="currentUser"
    class="mb-4 px-0"
    fluid
  >
    <BRow
      class="mb-3"
    >
      <slot
        name="header-actions"
        v-bind="gridTasks"
      />
    </BRow>

    <BRow
      v-if="gridTasks.busy"
      class="mb-3"
    >
      <BCol
        cols="12"
      >
        <div class="text-center py-5">
          <BIcon
            icon="stopwatch"
            font-scale="5"
            animation="cylon"
          />
          <p class="h3 py-2">
            <strong>Loading . . .</strong>
          </p>
        </div>
      </BCol>
    </BRow>

    <BRow
      v-else-if="gridTasks.rows.length === 0"
    >
      <BCol
        cols="12"
        class="text-center py-5"
      >
        <BIcon
          icon="calendar"
          font-scale="5"
        />
        <p class="h3 py-2">
          <strong>No tasks found</strong>
        </p>
      </BCol>
    </BRow>

    <BRow
      v-else
      class="gap-y-2 mb-3"
    >
      <BCol
        v-for="(task, index) in gridTasks.rows"
        :key="index"
        cols="12"
        md="4"
      >
        <TaskMoreDetails
          :activity="{
            id: task.id,
            activity_name: task?.activity.activity_name,
            conversion_rate: task.conversion_rate,
            total_buyers: task.customers_bought_count
          }"
          :current-progress="{
            event: task.events_count,
            customer: task.customers_attended_count,
            hectare: task.total_actual_hectares,
            cost: task.total_actual_costs,
            sales: task.total_actual_sales
          }"
          :total-progress="{
            event: task.event_plan,
            customer: task.total_customer_plan,
            hectare: task.total_hectare_plan,
            cost: task.total_budget_plan,
            sales: task.total_sales_plan
          }"
        >
          <template #footer>
            <div class="d-flex justify-content-between align-items-center gap-2 mt-3">
              <div class="d-flex gap-2">
                <BBadge variant="dark-light">
                  {{ monthShortFormatter(task.schedule) }}
                </BBadge>
              </div>
              <div class="align-self-end d-flex gap-2">
                <BButton
                  size="sm"
                  variant="primary"
                  @click.prevent="createEvent(task)"
                >
                  Record Event
                </BButton>
                <BButton
                  size="sm"
                  variant="outline-dark"
                  :to="{
                    name: 'fso.to-dos.task-events',
                    params: {
                      task: task.id
                    }
                  }"
                >
                  View Details
                  <i class="ml-1 far fa-arrow-right" />
                </BButton>
              </div>
            </div>
          </template>
        </TaskMoreDetails>
      </BCol>
    </BRow>

    <BModal
      ref="modalCreateEvent"
      size="lg"
      title="Task"
      scrollable
      centered
      hide-header
      no-close-on-esc
      no-enforce-focus
      no-close-on-backdrop
      content-class="scrollable-tabs"
      modal-class="full-height modal-no-padding"
      aria-hidden="false"
      role="dialog"
      @shown="eventModalShown"
      @hidden="eventModalHidden"
    >
      <BTabs
        active-nav-item-class="font-weight-bold text-primary"
        nav-class="scrollable-x-tabs no-scrollbar nav-event-tabs gap-x-1"
        content-class="scrollable"
        pills
      >
        <template #tabs-start>
          <div class="nav-item">
            <div
              class="d-flex align-items-center position-absolute"
              style="padding: 2px 1px; height: 38px;"
            >
              <i
                class="fas fa-chevron-left bg-primary-light p-1 rounded-lg"
                style="font-size: 20px;"
              />
            </div>
          </div>
        </template>
        <template #tabs-end>
          <div class="nav-item">
            <div
              class="d-flex align-items-center position-absolute"
              style="right: 20px; padding: 2px 1px; height: 38px;"
            >
              <i
                class="fas fa-chevron-right bg-primary-light p-1 rounded-lg"
                style="font-size: 20px;"
              />
            </div>
          </div>
        </template>
        <BTab
          class="p-2 min-h-400px"
        >
          <template #title>
            <div class="text-md-nowrap">
              <i
                :class="['fa-xl fad fa-file-lines', {
                  'text-danger fat fa-shake': vTabEventDetails()
                }]"
              />
              <b
                :class="['is-tab-active ml-1', {
                  'text-danger': vTabEventDetails()
                }]"
              >
                Details
              </b>
            </div>
          </template>

          <ValidationObserver
            ref="formEvent"
          >
            <form
              role="form"
              novalidate
              @submit.prevent
            >
              <BRow>

                <BCol
                  cols="12"
                  md="6"
                >
                  <ValidationProvider
                    v-slot="{ errors }"
                    vid="activity_id"
                    name="activity"
                    rules="required"
                  >
                    <BFormGroup
                      label="Activity"
                      label-for="fActivityName"
                      label-class="font-weight-bolder"
                      class="mb-2"
                    >
                      <BFormInput
                        id="fActivityName"
                        v-model="formEvent.activity_name"
                        type="text"
                        disabled
                      />
                      <div
                        v-if="(errors.length > 0)"
                        class="invalid-feedback"
                        v-text="errors[0]"
                      />
                    </BFormGroup>
                  </ValidationProvider>
                </BCol>

                <BCol
                  cols="12"
                  md="6"
                >
                  <ValidationProvider
                    v-slot="{ errors }"
                    vid="event_date"
                    name="date"
                    rules="required"
                  >
                    <BFormGroup
                      label="Event Date"
                      label-for="fEventDate"
                      label-class="font-weight-bolder"
                      class="mb-2"
                    >
                      <BFormInput
                        id="fEventDate"
                        v-model="formEvent.event_date"
                        type="date"
                        :min="formEventValidation.eventDate.min"
                        :max="formEventValidation.eventDate.max"
                        :state="errors.length > 0 ? false : null"
                        :disabled="state.busy || state.editing"
                      />
                      <div
                        v-if="(errors.length > 0)"
                        class="invalid-feedback"
                        v-text="errors[0]"
                      />
                    </BFormGroup>
                  </ValidationProvider>
                </BCol>

                <BCol cols="12">
                  <ValidationProvider
                    v-slot="{ errors }"
                    vid="customer_id"
                    name="customer"
                    :rules="{
                      required: Number(formEventValidation.customer) === 1
                    }"
                  >
                    <BFormGroup
                      label="Customer"
                      label-for="fCustomer"
                      label-class="font-weight-bolder"
                      class="mb-2"
                    >
                      <VSelect
                        v-model="selected.customer"
                        input-id="fCustomer"
                        label="customer_name"
                        append-to-body
                        placeholder="select customer here"
                        :options="options.customers"
                        :disabled="[
                          fetching.customers,
                          state.busy,
                          state.editing,
                          Number(formEventValidation.customer) === 0
                        ].some(isTrue => isTrue)"
                        :loading="fetching.customers"
                        :class="{ 'is-invalid': errors.length > 0 }"
                        @search="searchCustomers"
                      >
                        <template #option="{ customer_name, mobile_number, address }">
                          <div class="d-flex flex-column flex-md-row py-1">
                            <div class="flex-grow-1 d-flex align-items-center">
                              <div class="mr-2">
                                <BAvatar
                                  :text="String(customer_name).charAt(0)"
                                  variant="primary"
                                  size="50px"
                                />
                              </div>
                              <div class="d-flex flex-column">
                                <div
                                  class="text-break text-wrap font-weight-bolder"
                                  v-text="customer_name"
                                />
                                <strong class="mb-2">
                                  {{ mobile_number }}
                                </strong>
                                <div
                                  v-if="address"
                                  class="text-break text-wrap text-muted small"
                                >
                                  {{ address }}
                                </div>
                              </div>
                            </div>
                          </div>
                        </template>
                        <template #selected-option="{ customer_name }">
                          <div class="d-flex flex-column flex-md-row">
                            <div class="flex-grow-1 d-flex align-items-center">
                              <div class="mr-2">
                                <BAvatar
                                  :text="String(customer_name).charAt(0)"
                                  variant="primary"
                                  size="28px"
                                />
                              </div>
                              <div
                                class="w-200px w-lg-300px text-truncate"
                                v-text="customer_name"
                              />
                            </div>
                          </div>
                        </template>
                        <template #no-options="">
                          no available customer
                        </template>
                      </VSelect>
                      <div
                        v-if="(errors.length > 0)"
                        class="invalid-feedback"
                        v-text="errors[0]"
                      />
                    </BFormGroup>
                  </ValidationProvider>
                </BCol>

                <BCol
                  cols="12"
                  md="6"
                >
                  <ValidationProvider
                    v-slot="{ errors }"
                    vid="province_id"
                    name="province"
                    rules="required"
                  >
                    <BFormGroup
                      label="Province"
                      label-for="fProvince"
                      label-class="font-weight-bolder"
                      class="mb-2"
                    >
                      <VSelect
                        v-model="selected.province"
                        input-id="fProvince"
                        label="province_name"
                        append-to-body
                        placeholder="select province here"
                        :options="options.provinces"
                        :disabled="fetching.provinces || state.busy || state.editing"
                        :loading="fetching.provinces"
                        :calculate-position="calculateDropPosition"
                        :class="{ 'is-invalid': errors.length > 0 }"
                        @search="searchProvinces"
                      >
                        <template #option="{ province_name, region }">
                          <div
                            class="text-break text-wrap"
                            v-text="province_name"
                          />
                          <div
                            v-if="region"
                            class="text-muted small"
                            v-text="region?.region_name"
                          />
                        </template>
                        <template #selected-option="{ province_name }">
                          <div
                            class="w-200px w-lg-300px text-truncate"
                            v-text="province_name"
                          />
                        </template>
                        <template #no-options="">
                          no available province
                        </template>
                      </VSelect>
                      <div
                        v-if="(errors.length > 0)"
                        class="invalid-feedback"
                        v-text="errors[0]"
                      />
                    </BFormGroup>
                  </ValidationProvider>
                </BCol>

                <BCol
                  cols="12"
                  md="6"
                >
                  <ValidationProvider
                    v-slot="{ errors }"
                    vid="municipality_id"
                    name="municipality"
                    rules="required"
                  >
                    <BFormGroup
                      label="Municipality"
                      label-for="fMunicipality"
                      label-class="font-weight-bolder"
                      class="mb-2"
                    >
                      <VSelect
                        v-model="selected.municipality"
                        input-id="fMunicipality"
                        label="municipality_name"
                        append-to-body
                        placeholder="select municipality here"
                        :options="options.municipalities"
                        :disabled="state.busy || fetching.municipalities || fetching.provinces || state.editing"
                        :loading="fetching.municipalities"
                        :calculate-position="calculateDropPosition"
                        :class="{ 'is-invalid': errors.length > 0 }"
                        @search="searchMunicipalities"
                      >
                        <template #option="{ municipality_name }">
                          <div
                            class="text-break text-wrap"
                            v-text="municipality_name"
                          />
                        </template>
                        <template #selected-option="{ municipality_name }">
                          <div
                            class="w-200px w-lg-300px text-truncate"
                            v-text="municipality_name"
                          />
                        </template>
                        <template #no-options="">
                          no available municipality
                        </template>
                      </VSelect>
                      <div
                        v-if="(errors.length > 0)"
                        class="invalid-feedback"
                        v-text="errors[0]"
                      />
                    </BFormGroup>
                  </ValidationProvider>
                </BCol>

                <BCol cols="12">
                  <ValidationProvider
                    v-slot="{ errors }"
                    vid="remarks"
                    name="remarks"
                    rules="required|max:1000"
                  >
                    <BFormGroup
                      label="Remarks"
                      label-for="fRemarks"
                      label-class="font-weight-bolder"
                      class="mb-2"
                    >
                      <template #label>
                        <span class="d-flex justify-content-between">
                          Remarks
                          <span>
                            {{ formEvent.remarks ? formEvent.remarks.length : 0 }} / 1000
                          </span>
                        </span>
                      </template>
                      <BFormTextarea
                        id="fRemarks"
                        v-model="formEvent.remarks"
                        placeholder="enter remarks here"
                        rows="7"
                        max-rows="9999"
                        maxlength="1000"
                        :state="errors.length > 0 ? false : null"
                        :disabled="state.busy || state.editing"
                      />
                      <div
                        v-if="(errors.length > 0)"
                        class="invalid-feedback"
                        v-text="errors[0]"
                      />
                    </BFormGroup>
                  </ValidationProvider>
                </BCol>
              </BRow>
            </form>
          </ValidationObserver>
        </BTab>
        <BTab
          class="p-2 min-h-400px"
        >
          <template #title>
            <div class="text-md-nowrap">
              <i
                :class="['fa-xl fad fa-paperclip', {
                  'text-danger fat fa-shake': vTabAttachments()
                }]"
              />
              <b
                :class="['is-tab-active ml-1', {
                  'text-danger': vTabAttachments()
                }]"
              >
                Attachments
              </b>
            </div>
          </template>
          <Attachment
            ref="attachmentUploader"
            valid-id=""
            :loading="state.busy"
            :document-types="formEventValidation.documents"
            :path="formEvent.attachment_path"
            :files="formEvent.attachments"
            :deleted-files="formEvent.deleted_files"
            :show-uploader="!state.editing"
            @files:updated="updateEventAttachments"
          />
        </BTab>
        <BTab>
          <template #title>
            <div class="text-md-nowrap">
              <i class="fa-xl fad fa-map-marker-alt" />
              <b class="is-tab-active ml-1">
                Location
              </b>
            </div>
          </template>
          <GeoLocationMap
            ref="geoLocationMap"
            :coords="formEvent.geolocation"
            @updated:coords="updateEventGeolocation"
          />
        </BTab>
      </BTabs>
      <template #modal-footer="{ cancel }">
        <div class="w-100 d-flex justify-content-end align-items-center gap-2">
          <BButton
            v-if="!state.editing"
            variant="primary"
            :disabled="state.busy"
            @click.prevent="validateEvent"
          >
            Record Event
          </BButton>
          <BButton

            variant="outline-dark"
            :disabled="state.busy"
            @click.prevent="discardBtnEvent => preventAriaHidden(discardBtnEvent, cancel)"
          >
            {{ state.editing ? 'Close' : 'Discard' }}
          </BButton>
        </div>
      </template>
    </BModal>

  </BContainer>
</template>
<script>
// import QUERY from 'lodash'
import MOMENT from 'moment'
import { mapGetters } from 'vuex'

import TaskMoreDetails from '@/views/fso/task-events/components/TaskMoreDetails.vue'
import Attachment from '@/views/fso/task-events/components/Attachment.vue'
import GeoLocationMap from '@/components/custom/GeoLocationMap.vue'

import MxMisc from '@/mixins/misc'
import MxFormatter from '@/mixins/formatter'
import MxImageUrl from '@/mixins/image-url'
import MActivities from '@/mixins/maintenances/activities'
import MProvinces from '@/mixins/locations/provinces'
import MMunicipalities from '@/mixins/locations/municipalities'
import MCustomers from '@/mixins/accounts/customers'

import { SFSOTask, SFSOTaskEvent } from '@/services/fso'

// import Products from '@/views/fso/task-events/components/Products.vue'

export default {
  name: 'FSOTasks',

  components: {
    TaskMoreDetails,
    // Products,
    Attachment,
    GeoLocationMap
  },

  mixins: [
    MActivities,
    MxMisc,
    MxFormatter,
    MxImageUrl,
    MProvinces,
    MMunicipalities,
    MCustomers
  ],

  props: {
    month: {
      type: String,
      default: () => MOMENT().format('YYYY-MM')
    },
    moreDetails: {
      type: Number,
      default: () => 0
    }
  },

  data () {
    return {
      geoLocationId: null,
      state: {
        busy: false,
        editing: false
      },
      formEventValidation: {
        customer_per_event: 0,
        customer: 0,
        documents: [],
        eventDate: {
          min: '',
          max: ''
        }
      },
      formEvent: {
        id: '',
        activity_id: '',
        activity_name: '',
        task_id: '',
        customer_id: '',
        event_date: '',
        province_id: '',
        municipality_id: '',
        remarks: '',
        attachment_path: '',
        attachments: [],
        deleted_files: [],
        products: [],
        active: 1,
        geolocation: {
          latitude: null,
          longitude: null
        }
      },
      gridTasks: {
        busy: false,
        filter: {
          search: '',
          month: this.month,
          moreDetails: this.moreDetails
        },
        filterOn: [],
        sortBy: 'id',
        sortDesc: false,
        sortDirection: 'asc',
        pageOptions: [10, 25, 50, 100],
        currentPage: 1,
        totalRows: 0,
        perPage: 10,
        rows: []
      }
    }
  },

  computed: mapGetters({
    currentUser: 'auth/user'
  }),

  watch: {

    'selected.customer' (customer) {
      this.formEvent.customer_id = customer?.id || ''

      // this.selected.province = QUERY.find(this.options.provinces, {
      //   id: customer?.province_id || 0
      // }) || ''
      // this.editable.municipality = customer?.municipality_id || ''
    },

    'selected.province' (province) {
      this.formEvent.province_id = province?.id || ''
    },

    'selected.municipality' (municipality) {
      this.formEvent.municipality_id = municipality?.id || ''
    },

    'gridTasks.filter.search' () {
      this.gridProvider()
    },

    'gridTasks.filter.month' () {
      this.gridProvider()
    }
  },

  mounted () {
    if (this.currentUser) {
      this.gridProvider()
    }
  },

  methods: {

    vTabEventDetails () {
      if (this.$refs.formEvent) {
        const formEventErrors = this.$refs.formEvent.errors
        return Object.keys(formEventErrors).some(
          errorKey => formEventErrors[errorKey]?.length > 0
        )
      }

      return false
    },

    vTabProducts () {
      // if (this.$refs.formEvent) {
      //   const formEventErrors = this.$refs.formEvent.errors

      //   return Object.keys(formEventErrors).some(
      //     errorKey => formEventErrors[errorKey]?.length > 0
      //   )
      // }

      return false
    },

    vTabAttachments () {
      if (this.$refs.attachmentUploader) {
        const formDocument = this.$refs.attachmentUploader.$refs.formDocument

        if (formDocument) {
          return Object.keys(formDocument.errors).some(
            errorKey => formDocument.errors[errorKey]?.length > 0
          )
        }
      }

      return false
    },

    async gridProvider () {
      this.gridTasks.busy = true

      const payload = {
        page: this.gridTasks.currentPage,
        per_page: this.gridTasks.perPage,
        search: this.gridTasks.filter.search,
        schedule: this.gridTasks.filter.month
      }

      return SFSOTask.get(payload).then(
        ({ data }) => {
          this.gridTasks.rows = data
          return data
        }
      ).catch(() => {
        this.gridTasks.totalRows = 0
        this.gridTasks.rows = []
        return []
      }).finally(() => {
        this.gridTasks.busy = false
      })
    },

    updateEventAttachments (attachment) {
      this.formEvent = Object.assign(this.formEvent, {
        attachment_path: attachment.attachment_path,
        attachments: attachment.attachments,
        deleted_files: attachment.deleted_files
      })
    },

    updateEventProducts (products) {
      this.formEvent.products = products
    },

    updateEventGeolocation (geolocation) {
      this.formEvent = Object.assign(this.formEvent, {
        geolocation
      })
    },

    createEvent (task) {
      this.state.editing = false

      const dateFormat = 'YYYY-MM-DD'
      const currentMonth = `${task.schedule}-01`
      const today = MOMENT()
      const month = MOMENT(MOMENT(currentMonth).format(dateFormat))

      if (month.isSame(today, 'month')) {
        this.formEvent.event_date = today.format(dateFormat)
      }

      this.formEventValidation.eventDate = Object.assign(this.formEventValidation.eventDate, {
        min: month.startOf('month').format(dateFormat),
        max: month.endOf('month').format(dateFormat)
      })

      const activityRules = task.activity.rules

      const customerPerEvent = task?.activity?.customer_per_event || 0

      this.formEventValidation = Object.assign(this.formEventValidation, {
        customer_per_event: customerPerEvent,
        customer: activityRules?.customer || 0,
        documents: Array.from(task?.activity?.documents || []).sort((a, b) => String(b.required) - String(a.required))
      })

      this.formEvent = Object.assign(this.formEvent, {
        id: '',
        task_id: task.id,
        activity_id: task.activity_id,
        activity_name: task.activity?.activity_name || '',
        customer_per_event: task?.activity?.customer_per_event || 0,
        customer_id: '',
        province_id: '',
        municipality_id: '',
        remarks: '',
        attachment_path: '',
        attachments: [],
        deleted_files: [],
        products: [],
        active: 1,
        geolocation: task?.geolocation || {
          latitude: 0,
          longitude: 0
        }
      })

      this.getProvinces(this)

      if (Number(activityRules.customer) === 1) {
        this.getCustomers(this)
      } else {
        this.selected.customer = ''
      }

      this.$refs.modalCreateEvent.show()
    },

    validateEvent () {
      this.$refs.formEvent.validate().then(
        validEvent => {
          if (!validEvent) {
            return this.swalInvalid()
          }

          const noAttachments = this.formEvent.attachments.length === 0

          const hasRequiredDocuments = this.formEventValidation.documents.filter(
            documentType => Number(documentType.required) === 1
          ).map(
            documentType => ({
              name: documentType.document_type_name,
              uploaded: this.formEvent.attachments.some(
                attachment => Number(attachment.document_type_id) === Number(documentType.id)
              )
            })
          )

          if (noAttachments || !hasRequiredDocuments.every(doc => doc.uploaded)) {
            const requiredDocuments = hasRequiredDocuments.filter(doc => !doc.uploaded).map(doc => doc.name).join(', ')

            this.$refs.attachmentUploader.$refs.formDocument.setErrors({
              attachments: `Please upload at least one attachment for ${requiredDocuments}.`
            })

            return this.swalInvalid()
          }

          this.swalConfirm({
            html: this.state.editing ? '<h6>Update this Event?</h6>' : '<h6>Record this Event?</h6>',
            confirmButtonText: this.state.editing ? 'Update' : 'Record',
            preConfirm: async () => {
              this.state.busy = true

              const createOrUpdate = async () => {
                return this.state.editing ? this.putEvent() : this.postEvent()
              }

              return createOrUpdate().catch(
                ({ message }) => {
                  if (message && message?.errors) {
                    if (this.$refs.attachmentUploader) {
                      this.$refs.attachmentUploader.$refs.formDocument.setErrors(message.errors)
                    }

                    this.$refs.formEvent.setErrors(message.errors)
                  }
                }
              ).finally(() => {
                this.state.busy = false
              })
            }
          })
        }
      )
    },

    async postEvent () {
      return SFSOTaskEvent.post(this.formEvent).then(
        ({ data }) => {
          this.swalSuccess(data.message).then(() => {
            this.$refs.modalCreateEvent.hide()

            this.gridProvider()
          })

          return data
        }
      )
    },

    eventModalHidden () {
      this.detachArrowScroller('.nav-event-tabs')
    },

    eventModalShown () {
      this.attachArrowScroller('.nav-event-tabs')

      this.$refs.geoLocationMap.initMap()
    }
  }
}
</script>
